import { AiFillEye } from "react-icons/ai";
import { FaDollarSign, FaGlobe } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { BsArrowUpShort } from "react-icons/bs";
import TableHeaderCell from "components/reusable/TableHeaderCell";
import ConfirmationModal from '../../components/reusable/ConfirmationModal';
import useToastContext from "hooks/useToastContext";
import { apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import { useState } from "react";

const AllEmployeeTable = ({ 
  employeeList, 
  page, 
  sort, 
  setSort, 
  handleView, 
  allEmployeeList,
  handleWageSetup,
  handleGeoIp
}) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [loadingStates, setLoadingStates] = useState({});

  // Action Modal State - handles employment status change
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    item: null,
    actionType: '', // 'change_employment'
    newStatus: ''
  });

  // Employment Options - including all statuses shown in image + inactive
  const employmentStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'paid_leave', label: 'Paid leave of absence' },
    { value: 'unpaid_leave', label: 'Unpaid leave of absence' },
    { value: 'terminated', label: 'Terminated' },
    { value: 'not_on_payroll', label: 'Not on payroll' },
    { value: 'deceased', label: 'Deceased' },
  ];

  // Color mapping for each employment status
  const getStatusColor = (status) => {
    const colorMap = {
      'active': 'bg-green-100 text-green-800 border-green-200',
      'inactive': 'bg-red-200 text-gray-800 border-gray-300',
      'paid_leave': 'bg-blue-100 text-blue-800 border-blue-200',
      'unpaid_leave': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'terminated': 'bg-red-200 text-red-800 border-red-200',
      'not_on_payroll': 'bg-gray-100 text-gray-800 border-gray-200',
      'deceased': 'bg-purple-100 text-purple-800 border-purple-200',
    };
    return colorMap[status] || 'bg-gray-200 text-gray-800 border-gray-200';
  };

  const handleColumnSort = (columnName, backendField) => {
    const newSortType = sort.sortBy === backendField && sort.sortType === "asc" ? "desc" : "asc";
    setSort({
      ...sort,
      sortColumn: columnName,
      sortBy: backendField,
      sortType: newSortType
    });
  };

  const getColumnSortIcon = (backendField) => {
    if (sort.sortBy === backendField) {
      return (
        <span className="ml-1">
          {sort.sortType === "asc" && <BsArrowUpShort className="w-4 h-4" />}
          {sort.sortType === "desc" && <BsArrowUpShort className="h-4 rotate-180 w-4" />}
        </span>
      );
    }
  };

  // COMMENTED: Status toggle functionality removed
  // const handleToggleStatus = async (item, newStatus) => { ... }

  // Handle employment status change
  const handleEmploymentStatusChange = async () => {
    const { item, newStatus } = actionModal;
    if (!item || !newStatus) return;

    try {
      setLoadingStates(prev => ({ ...prev, [`employment_${item._id}`]: true }));
      
      const path = `${apiPath.employeeEmployementStatue}/${item._id}`;
      const payload = { 
        employmentStatus: newStatus 
      };
      
      const result = await apiPut(path, payload);

      if (result?.status === 200) {
        notification.success(result?.data?.message || "Employment status updated successfully");
        allEmployeeList();
      } else {
        notification.error(result?.data?.message || "Failed to update employment status");
      }
    } catch (error) {
      console.error("Action error:", error);
      notification.error(error.message || "Failed to update employment status");
    } finally {
      setLoadingStates(prev => ({ ...prev, [`employment_${item._id}`]: false }));
      setActionModal({ isOpen: false, item: null, actionType: '', newStatus: '' });
    }
  };

  // Main handler for action modal confirmation
  const handleActionConfirm = () => {
    const { actionType, item, newStatus } = actionModal;
    
    if (actionType === 'change_employment') {
      handleEmploymentStatusChange();
    }
  };

  return (
    <div>
      <ConfirmationModal
        isOpen={actionModal.isOpen}
        onClose={() => {
          setActionModal({ isOpen: false, item: null, actionType: '', newStatus: '' });
        }}
        onConfirm={handleActionConfirm}
        title="Confirm Employment Status Change"
        message={`Are you sure you want to change this employee's status to "${actionModal.newStatus?.replace(/_/g, ' ')}"?`}
        confirmText="Confirm"
        cancelText={t("CANCEL")}
        isLoading={loadingStates[`employment_${actionModal.item?._id}`]}
        isDanger={
          actionModal.newStatus === 'terminated' || 
          actionModal.newStatus === 'deceased' || 
          actionModal.newStatus === 'inactive'
        }
      />

      <div className="p-3">
        <div className="overflow-x-auto rounded-lg border relative">
          <table className="w-full text-xs text-left text-[#6c6c6c] dark:text-gray-400">
            <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <TableHeaderCell text={t("SR_NO")} />
                <TableHeaderCell text={t("EMPLOYEE_ID")} />
                <th scope="col" className="py-3 px-4">
                  <div className="flex justify-start items-center"><span>{t("EMPLOYEE_NAME")}</span></div>
                </th>
                <th scope="col" className="py-3 px-4">
                  <div className="flex justify-start items-center"><span>{t("DEPARTMENT")}</span></div>
                </th>
                <th scope="col" className="py-3 px-4">
                  <div className="flex justify-start items-center"><span>{t("JOB_TITLE")}</span></div>
                </th>
                <th scope="col" className="py-3 px-4">
                  <div className="flex justify-start items-center"><span>{t("EMAIL_ID")}</span></div>
                </th>
                {/* COMMENTED: Status column removed */}
                {/* <th scope="col" className="py-3 px-4">
                  <div className="flex justify-start items-center"><span>{t("STATUS")}</span></div>
                </th> */}
                {/* Employment Status Column */}
                {/* Hire Date Column with Sorting */}
                <th
                  onClick={() => handleColumnSort("hire_date", "acceptedRejectedAt")}
                  scope="col"
                  className="py-3 px-4 cursor-pointer"
                >
                  <div className="flex justify-start items-center w-28">
                    <span>{t("HIRE_DATE")}</span>
                    {getColumnSortIcon("acceptedRejectedAt")}
                  </div>
                </th>
                <th scope="col" className="py-3 px-4">
                  <div className="flex justify-start items-center">
                    <span>Employment Status</span>
                  </div>
                </th>
                <th scope="col" className="py-3 px-4 text-center relative">
                  {t("O_ACTION")}
                </th>
              </tr>
            </thead>
            <tbody>
              {employeeList &&
                employeeList.map((item, i) => {
                  // COMMENTED: Status toggle removed
                  // const isActive = item?.Employee?.status === 'active';
                  const currentEmploymentStatus = item?.employmentStatus || 'active';
                  const isLoading = loadingStates[`employment_${item._id}`];
                  
                  return (
                    <tr key={i} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                      <th scope="row" className="py-4 px-4 border-r font-medium text-gray-900 dark:text-white">
                        {i + 1 + 10 * (page - 1)}
                      </th>
                      <td className="border-r py-4 px-4 text-gray-900">{currentEmploymentStatus === "terminated" ? `${item?.Company.name.substring(0, 3)}${item.Employee._id.toString().slice(-3)}` :item?.Employee?.employeeId}</td>
                      <td className="text-gray-900 py-4 px-4 border-r">{item?.Employee?.name}</td>
                      <td className="py-4 border-r px-4 text-gray-900">{item?.Department?.name}</td>
                      <td className="py-4 px-4 border-r text-gray-900">{item?.Job?.name}</td>
                      <td className="px-2 border-r py-4 text-gray-900">{item?.Employee?.email}</td>
                      
                      {/* COMMENTED: Status Toggle removed */}
                      {/* <td className="py-4 text-left text-gray-900 px-4 border-r">
                        <a ... > ... </a>
                      </td> */}

                      {/* Hire Date Column */}
                      <td className="py-4 text-left text-gray-900 px-4 border-r">
                        {item?.acceptedRejectedAt ? dayjs(item?.acceptedRejectedAt).format("MM-DD-YYYY h:mm A") : "N/A"}
                      </td>

                      {/* Employment Status Select Column */}
                      <td className="py-4 px-4 border-r">
                        <select
                          value={currentEmploymentStatus}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            if (newStatus === currentEmploymentStatus) return;
                            setActionModal({
                              isOpen: true,
                              item: item,
                              actionType: 'change_employment',
                              newStatus: newStatus
                            });
                          }}
                          disabled={isLoading || currentEmploymentStatus === "terminated"}
                          className={`px-1 py-1 text-xs font-medium rounded-full border-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${getStatusColor(currentEmploymentStatus)} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          style={{ minWidth: '140px' }}
                        >
                          {employmentStatusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {isLoading && (
                          <span className="ml-2 text-xs text-gray-500">Updating...</span>
                        )}
                      </td>

                      {/* Action Column - View and Wage Setup */}
                      <td className="py-4 text-gray-900 px-4 border-l">
                        <div className="flex justify-center items-center gap-2">
                          
                          {/* 1. Set Wage Icon */}
                          {currentEmploymentStatus === "terminated" ? null : <a 
                            title="Set Wage"
                            onClick={() => handleWageSetup && handleWageSetup(item)}
                            className="text-slate-600 hover:text-green-600 cursor-pointer"
                          >
                            <FaDollarSign className="w-5 h-5" />
                          </a>}

                          {currentEmploymentStatus === "terminated" ? null : <a
                            title="Add or block work-from-home / employee IP"
                            onClick={() => handleGeoIp && handleGeoIp(item)}
                            className="text-slate-600 hover:text-indigo-600 cursor-pointer flex items-center gap-1"
                          >
                            <FaGlobe className="w-5 h-5" />
                          </a>}

                          {/* 2. View Employee Icon */}
                          <a 
                            title="View employee"
                            onClick={() => handleView(item)}
                            className="text-slate-600 hover:text-blue-600 cursor-pointer"
                          >
                            <AiFillEye className="w-5 h-5" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              {isEmpty(employeeList) ? (
                <tr className="text-center dark:bg-gray-800 bg-white border-b dark:border-gray-700">
                  <td className="py-4 px-4 border-r" colSpan={10}>
                    {t("O_NO_RECORD_FOUND")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AllEmployeeTable;